/**
 * Client API
 * Uses standardized ApiResponse<T> wrapper for all endpoints
 */

import { apiGet, apiPost, apiPut, apiDelete, ApiError } from './api-response';
import { API, buildPath } from './api-endpoints';
import { Client, Communication } from '../types/client';
import { logger } from '@/services/logger.service';

// Get all clients
export async function getClients(): Promise<Client[]> {
  try {
    const data = await apiGet<{ clients: Client[]; total?: number }>(API.clients.list.path);
    return data.clients ?? [];
  } catch (error: any) {
    logger.error('Error fetching clients', { error: error.message });
    throw error;
  }
}

// Get single client
export async function getClient(id: string): Promise<Client> {
  try {
    const data = await apiGet<{ client: Client }>(buildPath(API.clients.getById.path, { id }));
    return data.client;
  } catch (error: any) {
    logger.error('Error fetching client', { id, error: error.message });
    throw error;
  }
}

// Create client
export async function createClient(clientData: Partial<Client>): Promise<Client> {
  try {
    const data = await apiPost<{ client: Client }>(API.clients.create.path, clientData);
    return data.client;
  } catch (error: any) {
    logger.error('Error creating client', { error: error.message });
    throw error;
  }
}

// Update client
export async function updateClient(id: string, clientData: Partial<Client>): Promise<Client> {
  try {
    const data = await apiPut<{ client: Client }>(buildPath(API.clients.update.path, { id }), clientData);
    return data.client;
  } catch (error: any) {
    logger.error('Error updating client', { id, error: error.message });
    throw error;
  }
}

// Delete client
export async function deleteClient(id: string): Promise<void> {
  try {
    await apiDelete<void>(buildPath(API.clients.delete.path, { id }));
  } catch (error: any) {
    logger.error('Error deleting client', { id, error: error.message });
    throw error;
  }
}

// Add communication to client
export async function addCommunication(
  clientId: string,
  communicationData: Partial<Communication>
): Promise<{ communication: Communication; client: Client }> {
  try {
    return await apiPost<{ communication: Communication; client: Client }>(
      `/clients/${clientId}/communications`,
      communicationData
    );
  } catch (error: any) {
    logger.error('Error adding communication', { clientId, error: error.message });
    throw error;
  }
}

// Seed clients
export async function seedClients(clients: Client[]): Promise<void> {
  try {
    const result = await apiPost<{ count: number }>('/seed-clients', { clients });
    logger.info(`Seeded ${result.count} clients`);
  } catch (error: any) {
    logger.error('Error seeding clients', { error: error.message });
    throw error;
  }
}

export const clientAPI = {
  getClients,
  getClient,
  createClient,
  updateClient,
  deleteClient,
  addCommunication,
  seedClients,
};
